import { useEffect, useState, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCspAccessTokenQuery } from '../data/customer';
import { SESSION_STORAGE_KEYS } from '../constants/sessionStorageKeys';
import { setTokenClaims, setAuthDetails } from '../store/slice/authSlice';
import { SLICES } from '../constants/slices';
import { CSPUrlsMap } from '../routes/CSPUrlsMap';
import { STATUS_CODE } from '../constants/verificationStatus';
import { BrandContext } from '../contextProviders/brandContext';
import { FeatureFlagContext } from '../contextProviders/featureFlagContext';
import { AUTH0_ERROR_MESSAGE } from '../constants/errorMessage';
import { nonSSOAuthorizationParams, ssoAuthorizationParams } from '../constants/auth0Params';
import { AUTHENTICATION_TYPES } from '../constants/constants';

export const useGetAuthDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { authenticationType, cspMiddlewareApiIdentifier } =
    useContext(BrandContext).activeBrandContext.cspConfiguration.auth0Config[process.env.REACT_APP_ENV];
  const { cspConfiguration, isCspConfigLoading } = useContext(FeatureFlagContext);
  const isLiteModeEnabled = cspConfiguration?.IsLiteModeEnabled;
  const fetchCspToken = isLiteModeEnabled === false && !isCspConfigLoading;
  const isSSOAuthentication = authenticationType === AUTHENTICATION_TYPES.SSO;
  const auth0Params = isSSOAuthentication ? ssoAuthorizationParams : nonSSOAuthorizationParams;

  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const {
    user,
    isAuthenticated: isAuth0Authenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    isLoading: isAuth0Loading,
    error: auth0Error,
    getIdTokenClaims
  } = useAuth0();

  const [auth0AccessToken, setAuth0AccessToken] = useState(
    sessionStorage.getItem(SESSION_STORAGE_KEYS.AUTH0_ACCESS_TOKEN) || ''
  );

  // Fetch CSP access token only if we have an Auth0 token
  const {
    data: cspAccessData,
    error: cspError,
    isLoading: isCspAccessLoading
  } = useGetCspAccessTokenQuery(sessionStorage.getItem(SESSION_STORAGE_KEYS.AUTH0_ACCESS_TOKEN), {
    skip: !sessionStorage.getItem(SESSION_STORAGE_KEYS.AUTH0_ACCESS_TOKEN) || !fetchCspToken
  });

  // Fetch and store Auth0 Access Token
  useEffect(() => {
    const fetchAuth0Token = async () => {
      let token;
      try {
        if (isSSOAuthentication) {
          token = await getAccessTokenSilently({
            authorizationParams: {
              audience: auth0Params?.audience, // Ensure this matches your API's identifier
              scope: auth0Params?.scope // Ensure this matches the required scope for your API endpoint
            }
          });
        } else {
          token = await getAccessTokenSilently();
        }

        if (token) {
          setAuth0AccessToken(token);
          sessionStorage.setItem(SESSION_STORAGE_KEYS.AUTH0_ACCESS_TOKEN, token);
          dispatch(
            setAuthDetails({
              ...authDetails,
              auth0AccessToken: token,
              isAuth0Authenticated: true
            })
          );
        }
      } catch (error) {
        if (isSSOAuthentication) {
          loginWithRedirect();
        } else {
          throw Error('Error fetching Auth0 token:', error);
        }
      }
    };

    if (isSSOAuthentication || (isAuth0Authenticated && !authDetails?.auth0AccessToken)) {
      fetchAuth0Token();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth0Authenticated, auth0AccessToken, getAccessTokenSilently]);

  // Store CSP token when received
  useEffect(() => {
    if (cspAccessData) {
      dispatch(
        setAuthDetails({
          ...authDetails,
          cspAccessToken: cspAccessData.Token,
          partnerNumber: cspAccessData.PartnerNumber,
          contactId: cspAccessData.ContactId,
          isCspAuthenticated: true
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cspAccessData, dispatch]);

  // Store token claims
  useEffect(() => {
    const fetchTokenClaims = async () => {
      const claims = await getIdTokenClaims();
      dispatch(
        setTokenClaims({
          tokenClaims: {
            tokenExpiry: claims?.exp
          }
        })
      );
    };
    if (!isAuth0Loading && isAuth0Authenticated) {
      fetchTokenClaims();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth0Authenticated]);

  // Store user claims
  useEffect(() => {
    const cspMiddlewareUrl = cspMiddlewareApiIdentifier;
    if (user && !location.pathname.includes(CSPUrlsMap.verifySms.url)) {
      dispatch(
        setTokenClaims({
          tokenClaims: {
            registrationInProgress: user?.[`${cspMiddlewareUrl}/claims/registration_in_progress`],
            verificationPendingEmail: user?.[`${cspMiddlewareUrl}/claims/verification_pending_email`],
            verificationPendingPhone: user?.[`${cspMiddlewareUrl}/claims/verification_pending_phone`],
            migratedUser: user?.[`${cspMiddlewareUrl}/claims/migrated_user`],
            phoneNumber: user?.[`${cspMiddlewareUrl}/claims/phone_number`],
            sub: user?.sub,
            email: user?.email,
            mfaSkip: user?.[`${cspMiddlewareUrl}/claims/mfa_skip`]
          }
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Log errors
  useEffect(() => {
    if (cspError) {
      if ('status' in cspError && cspError.status === STATUS_CODE.UNAUTHORIZED) {
        sessionStorage.clear();
        localStorage.clear();
        navigate(CSPUrlsMap.login.url, { replace: true });
      }
    }
  }, [cspError, navigate]);

  // Redirect to custom auth0 error page
  useEffect(() => {
    const authErrorMessage = auth0Error?.message || auth0Error?.error_description || '';
    if (
      authErrorMessage.includes(AUTH0_ERROR_MESSAGE.ACCESS_DENIED) ||
      authErrorMessage.includes(AUTH0_ERROR_MESSAGE.USER_BLOCKED)
    ) {
      navigate(`${CSPUrlsMap.authErrorPage.url}${location.search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth0Error, navigate]);

  return {
    isAuth0Authenticated,
    loginWithRedirect,
    auth0AccessToken,
    cspAccessToken: cspAccessData?.Token,
    isCspAccessLoading,
    auth0Error
  };
};
